/**
 * Quanto denaro il cliente ha gia' messo sul piatto.
 *
 * Non e' piu' un lock: fino al 2026-08-27 il primo pagamento congelava totale e costi delle
 * attivita', in database (guard_locked_itinerary_pricing / _activities) e in UI. Quella
 * regola non c'e' piu' — un viaggio pagato in parte continua a cambiare, e il TD deve poterlo
 * riprezzare. Il vincolo che resta e' il piano rate: cambiati i numeri, le rate vanno
 * riportate sul nuovo totale, e PaymentPlanCard non lascia proseguire finche' non quadrano.
 *
 * Quello che il database ancora impedisce e' solo riscrivere una rata gia' incassata
 * (guard_paid_installment) e ritirare un preventivo gia' pagato (revoke_itinerary_quote).
 *
 * Lo status dell'itinerario non serve a dedurre nulla di tutto questo, anzi inganna: con un
 * piano rate mark_installment_paid porta l'itinerario a 'completed' gia' al primo acconto.
 *
 * ⚠️ La rata ancora aperta NON e' protetta: ribilanciando il piano si cambia l'importo della
 * rata per cui il cliente ha gia' un IBAN o una Checkout Session. Con i prezzi sbloccati il
 * caso diventa piu' frequente, non meno. Vedi guide/v2/10_PAYMENTS.md, "Buchi noti".
 */

import { ItineraryStatus } from '../models/itinerary.model';
import { IPaymentPlan } from '../models/payment-plan.model';
import { hasPaymentInFlight } from './payment-plan.utils';

/**
 * Il cliente ha impegnato denaro: incassato, oppure un tentativo ancora aperto.
 *
 * Soglia bassa, e comprende `payment_processing` di proposito: mentre un bonifico e' in
 * viaggio l'importo e' gia' stato comunicato al cliente insieme all'IBAN, quindi cambiare i
 * prezzi adesso e' una decisione che va presa sapendolo — e va seguita da una parola al
 * cliente, non solo dal riallineamento delle rate.
 *
 * Serve solo a scegliere il messaggio del banner: non disabilita piu' nessun campo. Da non
 * confondere con `hasCollectedMoney`, che ha la soglia piu' alta (incasso confermato) ed e'
 * quella del ritiro del preventivo.
 */
export const hasCommittedMoney = (plan: IPaymentPlan | null | undefined): boolean =>
    hasCollectedMoney(plan) || hasPaymentInFlight(plan);

/**
 * Il viaggiatore ha davvero incassato: soglia del **ritiro del preventivo**, piu' alta di
 * quella di `hasCommittedMoney`.
 *
 * La differenza e' tutta in `payment_processing`, ed e' voluta. Se contasse anche qui, un
 * cliente che apre il checkout sul bonifico e non conclude toglierebbe al TA la possibilita'
 * di ritirare il preventivo — che e' esattamente la via d'uscita da quella situazione: il
 * ritiro chiude i tentativi aperti (`revoke_itinerary_quote` li porta a 'expired'). Rispecchia
 * `itinerary_has_payments()` lato database.
 */
export const hasCollectedMoney = (plan: IPaymentPlan | null | undefined): boolean =>
    Boolean(plan?.installments.some((inst) => inst.status === 'paid'));

/**
 * Il viaggio e' saldato: non resta nulla da incassare, e per il TD e' un viaggio chiuso.
 *
 * Lo status 'completed' da solo non lo dimostra — con un piano rate ci arriva gia' al primo
 * acconto, e un viaggio con il solo acconto versato e' tutt'altro che concluso. Quando il
 * piano c'e' comanda il piano; lo status resta il ripiego per gli itinerari senza piano,
 * dove l'incasso e' stato registrato altrove.
 *
 * Le rate annullate non contano: sono importi che il cliente non deve piu'.
 */
export const isItinerarySettled = (
    plan: IPaymentPlan | null | undefined,
    status?: ItineraryStatus,
): boolean => {
    const active = plan?.installments.filter((inst) => inst.status !== 'cancelled') ?? [];
    if (active.length > 0) return active.every((inst) => inst.status === 'paid');
    return status === 'completed';
};

/**
 * La rata attualmente aperta al pagamento: la prima ancora da saldare. Le rate si pagano in
 * ordine, quindi e' l'unica su cui il cliente possa avere un pagamento in corso — e l'unica,
 * fra quelle non incassate, che va protetta insieme a loro.
 */
export const openInstallmentId = (plan: IPaymentPlan | null | undefined): string | null =>
    plan?.installments.find((inst) => inst.status === 'pending')?.id ?? null;
